import moment from "moment";
import { FC, useState, useEffect } from "react";
import { PiCalendar, PiTruck } from "react-icons/pi";
import { Text, Title } from "rizzui";
import { DatePicker } from "../../../../components/forms/DatePicker";
import cn from "../../../../utils/classNames";
import { es } from 'date-fns/locale';
import './driver-header-mobile.css';

interface DriverHeaderProps {
    selectedDate: string;
    onDateChange: (date: string) => void;
}

const DriverHeader: FC<DriverHeaderProps> = ({ selectedDate, onDateChange }) => {
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    // Detectar si es dispositivo móvil
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const handleDatePickerChange = (date: Date | null) => {
        if (date) {
            const formattedDate = moment(date).format('YYYY-MM-DD');
            onDateChange(formattedDate);
            setShowDatePicker(false);
        }
    };

    // Formatear fecha de manera consistente para móvil y desktop
    const formatDisplayDate = (dateString: string) => {
        const formatted = moment(dateString).format('dddd, DD [de] MMMM [de] YYYY');
        return formatted.charAt(0).toUpperCase() + formatted.slice(1);
    };

    return (
        <div className="bg-white shadow-sm border-b sticky top-5 z-10 rounded-sm">
            <div className="px-3 sm:px-4 py-3 sm:py-4">
                <div className="flex items-center justify-between mb-3 sm:mb-4">
                    <div className="flex-1 min-w-0">
                        <Title as="h1" className="text-lg sm:text-xl font-bold text-gray-900 truncate">
                            Panel de Conductor
                        </Title>
                        <Text className="text-xs sm:text-sm text-gray-500 mt-1 leading-tight">
                            {isMobile 
                                ? moment(selectedDate).format('DD/MM/YYYY')
                                : formatDisplayDate(selectedDate)
                            }
                        </Text>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <PiTruck className="h-5 w-5 sm:h-6 sm:w-6 text-black" />
                    </div>
                </div>

                {/* Selector de fecha mejorado para móvil */}
                <div className="flex flex-col gap-2 sm:gap-3">
                    {/* Navegación rápida por días - Optimizada para móvil */}
                    <div className="flex gap-1 sm:gap-2 overflow-x-auto pb-2 date-selector scrollbar-hide">
                        {[-1, 0, 1, 2].map(offset => {
                            const date = moment().add(offset, 'days');
                            const dateStr = date.format('YYYY-MM-DD');
                            const isSelected = selectedDate === dateStr;
                            const isToday = offset === 0;

                            return (
                                <button
                                    key={offset}
                                    onClick={() => onDateChange(dateStr)}
                                    className={cn(
                                        "flex-shrink-0 px-3 py-2 sm:px-4 sm:py-2 rounded-lg border text-xs sm:text-sm font-medium transition-all duration-200 touch-manipulation",
                                        "min-w-[70px] sm:min-w-[80px] h-12 sm:h-auto", // Altura mínima para táctil
                                        "active:scale-95 active:bg-opacity-80", // Feedback táctil
                                        isSelected
                                            ? "bg-primary text-white border-primary shadow-md transform scale-105"
                                            : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                                    )}
                                    style={{ 
                                        WebkitTapHighlightColor: 'transparent',
                                        touchAction: 'manipulation'
                                    }}
                                >
                                    <div className="text-center">
                                        <div className="font-medium leading-tight">
                                            {isToday ? 'Hoy' : date.format('ddd').charAt(0).toUpperCase() + date.format('ddd').slice(1)}
                                        </div>
                                        <div className="text-xs opacity-75 mt-0.5">
                                            {date.format('DD/MM')}
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                        
                        {/* Botón para abrir date picker - Solo en desktop */}
                        {!isMobile && (
                            <button
                                onClick={() => setShowDatePicker(!showDatePicker)}
                                className={cn(
                                    "flex-shrink-0 px-3 py-2 sm:px-4 sm:py-2 rounded-lg border text-xs sm:text-sm font-medium transition-all duration-200 touch-manipulation",
                                    "min-w-[90px] sm:min-w-[100px] h-12 sm:h-auto flex items-center justify-center gap-1 sm:gap-2",
                                    "active:scale-95 active:bg-opacity-80", // Feedback táctil
                                    showDatePicker
                                        ? "bg-primary text-white border-primary shadow-md"
                                        : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                                )}
                                style={{ 
                                    WebkitTapHighlightColor: 'transparent',
                                    touchAction: 'manipulation'
                                }}
                            >
                                <PiCalendar className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                                <span className="truncate">Otra fecha</span>
                            </button>
                        )}
                    </div>

                    {/* Botón "Otra fecha" para móvil - Debajo de las opciones de fecha */}
                    {isMobile && (
                        <div className="flex justify-center">
                            <button
                                onClick={() => setShowDatePicker(!showDatePicker)}
                                className={cn(
                                    "px-4 py-3 rounded-lg border text-sm font-medium transition-all duration-200 touch-manipulation",
                                    "w-full max-w-xs flex items-center justify-center gap-2",
                                    "active:scale-95 active:bg-opacity-80", // Feedback táctil
                                    showDatePicker
                                        ? "bg-primary text-white border-primary shadow-md"
                                        : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                                )}
                                style={{ 
                                    WebkitTapHighlightColor: 'transparent',
                                    touchAction: 'manipulation'
                                }}
                            >
                                <PiCalendar className="h-4 w-4 flex-shrink-0" />
                                <span>Seleccionar otra fecha</span>
                            </button>
                        </div>
                    )}

                    {/* Date Picker desplegable - Mejorado para móvil */}
                    {showDatePicker && (
                        <div className="bg-gray-50 p-3 sm:p-4 rounded-lg border shadow-lg">
                            <Text className="text-sm font-medium text-gray-700 mb-3">
                                Seleccionar fecha específica:
                            </Text>
                            <div className="space-y-3">
                                <DatePicker
                                    selected={moment(selectedDate).toDate()}
                                    onChange={handleDatePickerChange}
                                    dateFormat="dd/MM/yyyy"
                                    locale={es}
                                    inputProps={{
                                        inputClassName: cn(
                                            'h-11 sm:h-9 text-base sm:text-sm [&_input]:text-ellipsis bg-white border-gray-300',
                                            'focus:border-primary focus:ring-2 focus:ring-primary/20',
                                            'touch-manipulation' // Optimización táctil
                                        ),
                                    }}
                                    className="w-full"
                                    placeholderText="Seleccionar fecha"
                                    popperPlacement={isMobile ? "bottom" : "bottom-start"}
                                    maxDate={moment().add(1, 'year').toDate()}
                                    minDate={moment().subtract(1, 'year').toDate()}
                                    calendarClassName={cn(
                                        "shadow-xl border-0 rounded-lg",
                                        isMobile && "!w-full max-w-sm mx-auto"
                                    )}
                                    dayClassName={(date: Date) => cn(
                                        "hover:bg-primary hover:text-white transition-colors",
                                        isMobile && "!w-10 !h-10 !text-sm" // Tamaño táctil para móvil
                                    )}
                                />
                                
                                {/* Botones de acción - Mejorados para móvil */}
                                <div className="flex gap-2 pt-2">
                                    <button
                                        onClick={() => setShowDatePicker(false)}
                                        className={cn(
                                            "flex-1 sm:flex-none px-4 py-2.5 sm:py-2 text-sm bg-gray-200 text-gray-700 rounded-lg",
                                            "hover:bg-gray-300 transition-colors touch-manipulation",
                                            "active:scale-95 active:bg-gray-400", // Feedback táctil
                                            "min-h-[44px] sm:min-h-[36px]" // Altura mínima táctil
                                        )}
                                        style={{ 
                                            WebkitTapHighlightColor: 'transparent',
                                            touchAction: 'manipulation'
                                        }}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        onClick={() => {
                                            onDateChange(moment().format('YYYY-MM-DD'));
                                            setShowDatePicker(false);
                                        }}
                                        className={cn(
                                            "flex-1 sm:flex-none px-4 py-2.5 sm:py-2 text-sm bg-primary text-white rounded-lg",
                                            "hover:bg-primary/90 transition-colors touch-manipulation",
                                            "active:scale-95 active:bg-primary/80", // Feedback táctil
                                            "min-h-[44px] sm:min-h-[36px]" // Altura mínima táctil
                                        )}
                                        style={{ 
                                            WebkitTapHighlightColor: 'transparent',
                                            touchAction: 'manipulation'
                                        }}
                                    >
                                        Hoy
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DriverHeader;